import { useEffect, useMemo, useState } from "react";
import { useRecoilState } from "recoil";

import { useReportMenuItems } from "@ryde/redux";
import {
  IReportTemplateFilters,
  isOperatorReportFilterType,
  ReportsApi,
} from "@ryde/shared";

import { ANALYTIC_REPORT_LABEL_KEYS } from "../dynamic-dashboard.analytic-reports";
import { dashboardFilterSchemaByReportAtom } from "../dynamic-dashboard.atoms";
import { IReportSchemaState } from "../dynamic-dashboard.filters.utils";

interface IDashboardFilterSchema {
  /** Every analytic report's filters merged — the widget config the bar renders chips from. */
  schema: IReportTemplateFilters;
  schemaState: IReportSchemaState;
}

export const useDashboardFilterSchema = (): IDashboardFilterSchema => {
  const { reportMenuItems } = useReportMenuItems();

  const reportIds = useMemo(
    () =>
      (reportMenuItems ?? [])
        .filter(group => ANALYTIC_REPORT_LABEL_KEYS[group.reportTemplateId])
        .map(group => group.reportItems[0]?.id)
        .filter((id): id is number => typeof id === "number"),
    [reportMenuItems],
  );
  const reportIdsKey = reportIds.join(",");

  const [byReport, setByReport] = useRecoilState(
    dashboardFilterSchemaByReportAtom,
  );
  const [isSettled, setIsSettled] = useState(false);
  const [failedReportIds, setFailedReportIds] = useState<number[]>([]);

  useEffect(() => {
    if (!reportIds.length) {
      return undefined;
    }

    const abort = new AbortController();
    let cancelled = false;
    setIsSettled(false);

    const run = async (): Promise<void> => {
      const results = await Promise.all(
        reportIds.map(id =>
          ReportsApi.fetchReportData({ id, limit: 1 }, abort)
            .then(result => ({
              id,
              filters:
                result?.success && result.data
                  ? (result.data.reportTemplate?.availableFilters ?? {})
                  : undefined,
            }))
            .catch(
              (): {
                id: number;
                filters: IReportTemplateFilters | undefined;
              } => ({
                id,
                filters: undefined,
              }),
            ),
        ),
      );
      if (cancelled) {
        return;
      }

      const loaded: Record<number, IReportTemplateFilters> = {};
      results.forEach(({ id, filters }) => {
        if (filters) {
          loaded[id] = filters;
        }
      });
      setByReport(loaded);
      setFailedReportIds(
        results.filter(({ filters }) => !filters).map(({ id }) => id),
      );
      setIsSettled(true);
    };

    run();

    return () => {
      cancelled = true;
      abort.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reportIdsKey captures reportIds
  }, [reportIdsKey]);

  const schema = useMemo(
    () =>
      Object.values(byReport).reduce<IReportTemplateFilters>(
        (merged, filters) => {
          Object.entries(filters).forEach(([templateKey, settings]) => {
            // Operator-typed filters (RYDE-8067) are excluded from the BAR only: their values are sent
            // as strict criteria.where descriptors, and the bar fans one filter bag out to every card —
            // a card whose template lacks the key would get a 422 instead of the legacy silent-drop the
            // bar relies on. `byReport` keeps declaring them, so the per-card prune logic that deletes
            // user state still sees the truth about what each report supports.
            if (!isOperatorReportFilterType(settings?.filterFieldTypeId)) {
              merged[templateKey] = settings;
            }
          });

          return merged;
        },
        {},
      ),
    [byReport],
  );

  const schemaState = useMemo(
    () => ({ byReport, isSettled, failedReportIds }),
    [byReport, isSettled, failedReportIds],
  );

  return { schema, schemaState };
};
